import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';

/** Monogram: a sun setting on the horizon over two swells. */
export function LogoMark({ className, ...props }: ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 40 40" fill="none" aria-hidden className={cn('size-9', className)} {...props}>
      <circle cx="20" cy="20" r="19" stroke="currentColor" strokeOpacity="0.45" />
      <path d="M11 22a9 9 0 0 1 18 0" stroke="currentColor" strokeWidth="1.4" />
      <path d="M7 22h26" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path
        d="M9 26.5c2.2 0 2.2-1.5 4.4-1.5s2.2 1.5 4.4 1.5 2.2-1.5 4.4-1.5 2.2 1.5 4.4 1.5 2.2-1.5 4.4-1.5"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
      <path
        d="M12 30c1.6 0 1.6-1 3.2-1s1.6 1 3.2 1 1.6-1 3.2-1 1.6 1 3.2 1 1.6-1 3.2-1"
        stroke="currentColor"
        strokeOpacity="0.6"
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface LogoProps extends ComponentProps<'span'> {
  /** Small caption under the wordmark, e.g. "Admin". */
  caption?: ReactNode;
}

export function Logo({ className, caption, ...props }: LogoProps) {
  return (
    <span className={cn('inline-flex items-center gap-3 text-primary', className)} {...props}>
      <LogoMark />
      <span className="flex flex-col leading-none">
        <span className="font-display text-xl tracking-[0.32em] text-fg uppercase">
          Duva<span className="text-primary">Marine</span>
        </span>
        {caption ? (
          <span className="eyebrow mt-1.5 !text-[0.6rem] !text-muted">{caption}</span>
        ) : null}
      </span>
    </span>
  );
}

export function Eyebrow({ className, ...props }: ComponentProps<'span'>) {
  return <span className={cn('eyebrow', className)} {...props} />;
}

/** Thin horizon + swell motif used as a section divider. Purely decorative. */
export function HorizonDivider({ className, ...props }: ComponentProps<'svg'>) {
  return (
    <svg
      viewBox="0 0 600 24"
      preserveAspectRatio="none"
      aria-hidden
      className={cn('h-6 w-full text-primary', className)}
      {...props}
    >
      <path d="M0 8h600" stroke="currentColor" strokeOpacity="0.35" strokeWidth="0.75" />
      <path
        d="M0 16c25 0 25-5 50-5s25 5 50 5 25-5 50-5 25 5 50 5 25-5 50-5 25 5 50 5 25-5 50-5 25 5 50 5 25-5 50-5 25 5 50 5 25-5 50-5 25 5 50 5"
        stroke="currentColor"
        strokeOpacity="0.18"
        strokeWidth="0.75"
        fill="none"
      />
    </svg>
  );
}
